import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Table, TableBody, TableCell, TableHead, TableHeadCell, TableRow, Button } from 'flowbite-react';
import { PencilSquareIcon, TrashIcon } from '@heroicons/react/24/outline';
import Modals from '../../components/Modal';
import FormClient from './FormClient';
import LoadingSpinner from '../../components/LoadingSpinner';

const Clients = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState('');
  const [clientId, setClientId] = useState(null);
  const [clients, setClients] = useState([]);
  const [error, setError] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const openModal = (title, id, createMode) => {
    setModalTitle(title);
    setClientId(id);
    setIsCreating(createMode);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    fetchClients();
  };

  const fetchClients = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(
        `${((import.meta.env.VITE_ENV == 'development') ? import.meta.env.VITE_BACKEND_LOCAL : import.meta.env.VITE_BACKEND)}/clients`,
        { withCredentials: true },
      );
      setClients(response.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchClients();
  }, []);

  const handleFormSubmit = async (data) => {
    setIsLoading(true);
    try {
      const response = await axios.post(
        `${((import.meta.env.VITE_ENV == 'development') ? import.meta.env.VITE_BACKEND_LOCAL : import.meta.env.VITE_BACKEND)}/client/create`,
        data,
        { withCredentials: true },
      );
      setClients([...clients, response.data.client]);
    } catch (error) {
      console.error('Error al crear cliente:', error);
      alert('Error al crear cliente');
    } finally {
      setIsLoading(false);
    }
    setIsModalOpen(false);
  };

  const handleFormUpdate = async (data) => {
    setIsLoading(true);
    try {
      const response = await axios.put(
        `${((import.meta.env.VITE_ENV == 'development') ? import.meta.env.VITE_BACKEND_LOCAL : import.meta.env.VITE_BACKEND)}/client/${clientId}/update`,
        data,
        { withCredentials: true },
      );
      setClients(
        clients.map((c) =>
          c.id === clientId ? { ...c, ...response.data.client } : c,
        ),
      );
    } catch (error) {
      console.error('Error al actualizar cliente:', error);
      alert('Error al actualizar cliente');
    } finally {
      setIsLoading(false);
    }
    setIsModalOpen(false);
  };

  const handleRemove = async (id) => {
    const confirma = confirm('Confirma la eliminación del cliente?');
    if (!confirma) return;
    setIsLoading(true);
    try {
      await axios.delete(
        `${((import.meta.env.VITE_ENV == 'development') ? import.meta.env.VITE_BACKEND_LOCAL : import.meta.env.VITE_BACKEND)}/client/${id}/delete`,
        { withCredentials: true },
      );
      setClients(clients.filter((c) => c.id !== id));
    } catch (error) {
      console.error('Error al eliminar cliente:', error);
      alert('Error al eliminar cliente');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {isLoading && <LoadingSpinner message="Procesando..." size="lg" color="blue" />}
      <div className="p-2 mt-4 flex justify-between">
        <h1 className="text-xl mb-2 px-3 flex-col">Clientes</h1>
        <div className="flex justify-end">
          <Button
            className="text-gray-600"
            onClick={() => openModal('Nuevo cliente', null, true)}
            color="gray"
          >
            Nuevo cliente
          </Button>
        </div>
        {error && <div className="text-red-500">{error}</div>}
      </div>

      <div className="overflow-x-auto text-left">
        <Table>
          <TableHead>
            <TableHeadCell className="text-left">RUT</TableHeadCell>
            <TableHeadCell className="text-left">Razón social</TableHeadCell>
            <TableHeadCell className="text-left">Contacto</TableHeadCell>
            <TableHeadCell>
              <span className="srOnly">Acción</span>
            </TableHeadCell>
          </TableHead>
          <TableBody className="divide-y">
            {clients.map((client) => (
              <TableRow key={client.id} className="bg-white dark:border-gray-700 dark:bg-gray-800">
                <TableCell className="whitespace-nowrap font-medium text-gray-900 dark:text-white">
                  {client.rut}
                </TableCell>
                <TableCell>{client.business_name}</TableCell>
                <TableCell>{client.contact}</TableCell>
                <TableCell className="inline-flex">
                  <a href="#" onClick={() => openModal('Editar cliente', client.id, false)}>
                    <PencilSquareIcon className="w-5 text-blue-500 mr-3" title="Editar cliente" />
                  </a>
                  <a href="#" onClick={() => handleRemove(client.id)}>
                    <TrashIcon className="w-5 text-blue-500 mr-3" title="Eliminar cliente" />
                  </a>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Modals
        isOpen={isModalOpen}
        onClose={closeModal}
        title={modalTitle}
        modalSize="md"
      >
        <FormClient
          clientId={clientId}
          onSubmit={isCreating ? handleFormSubmit : handleFormUpdate}
          onClose={closeModal}
        />
      </Modals>
    </>
  );
};

export default Clients;

